import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import {
  FC, useCallback, useMemo, useRef,
} from 'react';
import Button from 'src/common/components/ButtonDS22';
import { DropdownOption } from 'src/common/components/DropdownDS22';
import { PATHS } from 'src/common/constants';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import { useListingsPageContext } from 'src/admin/listing-editor/providers/ListingsPageProvider';
import Icon from 'src/common/components/IconDS22';
import ActionsDropdown from 'src/common/components/ActionsDropdown';
import { EditorListing } from 'src/common/types/api/listings';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import DeleteModal from '../DeleteModal';
import SearchField from '../SearchField';
import SelectAll from '../SelectAll';
import styles from './ListingsHeader.module.scss';
import { isListingValid } from '../../utils/getListingProgress';
import { shouldSubmitListingForReview } from '../../helpers';
import ApprovalModal from '../ApprovalModal';

type Props = {
  hasPayoutMethod: boolean;
  openSnoozeModal: () => void;
};
const ListingsHeader: FC<Props> = ({ hasPayoutMethod, openSnoozeModal }) => {
  const {
    allSelected,
    ownerListings,
    selectedListings,
    selectAllListings,
    deselectAllListings,
    publishSelectedListings,
    unpublishSelectedListings,
    deleteSelectedListings,
    refresh,
    isImpersonatedSession,
  } = useListingsPageContext();
  const { close, isOpen, open } = useOpenCloseToggle();
  const {
    close: closeApprovalModal,
    isOpen: isApprovalModalOpen,
    open: openApprovalModal,
  } = useOpenCloseToggle();
  const [t] = useTranslation('admin-listings');
  const router = useRouter();
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const listingsToReviewRef = useRef<Pick<EditorListing, 'id' | 'shortname'>[]>([]);
  const listings = useMemo(() => (
    ownerListings.filter((listing) => selectedListings.includes(listing.id))
  ), [ownerListings, selectedListings]);
  const allListingsValidity = useMemo(() => listings.map(
    (listing) => isListingValid(listing, isImpersonatedSession),
  ), [isImpersonatedSession, listings]);

  const actionOptions = useMemo((): DropdownOption[] => {
    const listingsCount = listings.length;
    const hasPublishedListings = listingsCount > 0
      && listings.some((listing) => Boolean(listing.active));

    const hasUnpublishedListings = listingsCount > 0
      && listings.some((listing, i) => !listing.active && allListingsValidity[i]);

    return [
      {
        id: 'publish',
        dropdownLabel: t('landing-page--publish'),
        selectedLabel: t('landing-page--publish'),
        disabled: (!hasPayoutMethod && !isImpersonatedSession) || !hasUnpublishedListings,
      },
      {
        id: 'snooze',
        dropdownLabel: t('landing-page--snooze'),
        selectedLabel: t('landing-page--snooze'),
      },
      {
        id: 'unpublish',
        dropdownLabel: t('landing-page--unpublish'),
        selectedLabel: t('landing-page--unpublish'),
        disabled: !hasPublishedListings,
      },
      {
        id: 'delete',
        dropdownLabel: t('landing-page--delete'),
        selectedLabel: t('landing-page--delete'),
        disabled: listingsCount === 0,
      },
    ];
  }, [allListingsValidity, hasPayoutMethod, isImpersonatedSession, listings, t]);

  const indeterminate = !allSelected && ownerListings.length > 0 && selectedListings.length > 0;

  const handleSelectAllOnChange = useCallback((checked: boolean) => {
    if (checked) {
      selectAllListings();
    } else {
      deselectAllListings();
    }
  }, [deselectAllListings, selectAllListings]);

  const checkToReviewListings = useCallback(() => {
    const listingsForReview = listings.filter(
      (listing) => !listing.under_review
      && selectedListings.includes(listing.id)
      && shouldSubmitListingForReview(
        listing.active,
        listing.date_modified,
        listing.date_published,
      ) && isListingValid(listing, isImpersonatedSession),
    );
    if (selectedListings.length && listingsForReview.length) {
      listingsToReviewRef.current = listingsForReview.map(
        ({ id, shortname }) => ({ id, shortname }),
      );
      openApprovalModal();
    } else {
      listingsToReviewRef.current = [];
      publishSelectedListings();
    }
  }, [
    listings,
    selectedListings,
    isImpersonatedSession,
    openApprovalModal,
    publishSelectedListings,
  ]);

  const handleOnSelect = useCallback((option: DropdownOption) => {
    switch (option.id) {
      case 'publish': {
        checkToReviewListings();
        break;
      }
      case 'unpublish': {
        unpublishSelectedListings();
        break;
      }
      case 'delete': {
        open();
        break;
      }
      case 'snooze': {
        openSnoozeModal();
        break;
      }
      default: {
        const id = selectedListings[0];
        router.push(`${PATHS.ADMIN_LISTING_EDITOR}${id}`);
        break;
      }
    }
  }, [
    open,
    openSnoozeModal,
    router,
    selectedListings,
    unpublishSelectedListings,
    checkToReviewListings,
  ]);

  const handleDelete = useCallback(async () => {
    await deleteSelectedListings();
    close();
  }, [close, deleteSelectedListings]);

  const { setSearch, search } = useListingsPageContext();

  return (
    <div className={styles.root}>
      <div className={styles.leftView}>
        <SelectAll
          checked={allSelected}
          indeterminate={indeterminate}
          onChange={handleSelectAllOnChange}
        />
        <ActionsDropdown
          options={actionOptions}
          handleSelect={handleOnSelect}
        />
      </div>
      <div className={styles.rightView}>
        <div className={styles.search}>
          <SearchField search={search} onChange={setSearch} />
        </div>
        <div className={styles.addListing}>
          <Button
            type="link"
            disabled={!hasPayoutMethod}
            href={PATHS.ADMIN_LISTING_NEW}
            data-test="add-listing"
          >
            {t(isNavUpdateEnabled ? 'landing-page--add-boat' : 'landing-page--add-listing')}
            <Icon
              id="plus-circle"
              size="l"
            />
          </Button>
        </div>
      </div>
      <DeleteModal
        selectedListingsCount={selectedListings.length}
        handleDelete={handleDelete}
        open={isOpen}
        closeModal={close}
      />
      <ApprovalModal
        listings={listingsToReviewRef.current}
        close={closeApprovalModal}
        isOpen={isApprovalModalOpen}
        afterSubmit={refresh}
      />
    </div>
  );
};

export default ListingsHeader;
